import { useState } from 'react'
import { QUEUE_STRATEGIES } from '../../utils/queue'

/**
 * Cola de atención de un ticket, con la opción de enviarlo a otra cola.
 * Presentacional: la transferencia la resuelve `useQueueTransfer`.
 *
 * @component
 * @param {Object} props
 * @param {Object|null} props.queue - Cola actual (null si no tiene).
 * @param {Array<Object>} props.queues - Colas disponibles.
 * @param {boolean} props.canTransfer - Si el usuario puede transferir.
 * @param {boolean} props.transferring
 * @param {(queue: Object, reassign: boolean) => Promise<boolean>} props.onTransfer
 * @returns {JSX.Element}
 */
const TicketQueueCard = ({
  queue,
  queues,
  canTransfer,
  transferring,
  onTransfer,
}) => {
  const [open, setOpen] = useState(false)
  const [targetId, setTargetId] = useState('')
  const [reassign, setReassign] = useState(true)

  // Sólo colas activas distintas de la actual.
  const targets = queues.filter((q) => q.active && q.id !== queue?.id)
  const target = targets.find((q) => String(q.id) === String(targetId))

  const close = () => {
    setOpen(false)
    setTargetId('')
    setReassign(true)
  }

  const handleTransfer = async () => {
    if (!target) return
    const ok = await onTransfer(target, reassign)
    if (ok) close()
  }

  return (
    <div className="tqc">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="section-subtitle mb-0">Cola de atención</label>
        {canTransfer && !open && targets.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-decoration-none small"
            onClick={() => setOpen(true)}
          >
            Transferir
          </button>
        )}
      </div>

      {queue ? (
        <div className="tqc-current">
          <span className="tqc-dot" style={{ background: queue.color }} />
          <span className="tqc-name">{queue.name}</span>
          {!queue.active && <span className="tqc-badge">Inactiva</span>}
        </div>
      ) : (
        <div className="tqc-empty">Sin cola asignada</div>
      )}

      {open && (
        <div className="tqc-form">
          <select
            className="form-select form-select-sm"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            aria-label="Cola destino"
            autoFocus
          >
            <option value="">Seleccione la cola destino…</option>
            {targets.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>

          <label className="tqc-check">
            <input
              type="checkbox"
              className="form-check-input m-0"
              checked={reassign}
              onChange={(e) => setReassign(e.target.checked)}
            />
            <span>
              Reasignar en la cola destino
              {target && reassign && (
                <small className="d-block">
                  {QUEUE_STRATEGIES[target.strategy]?.hint}
                </small>
              )}
              {!reassign && (
                <small className="d-block">
                  Se mantiene el responsable actual.
                </small>
              )}
            </span>
          </label>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light flex-fill"
              onClick={close}
              disabled={transferring}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary flex-fill"
              onClick={handleTransfer}
              disabled={!target || transferring}
            >
              {transferring && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              Enviar
            </button>
          </div>
        </div>
      )}

      <style>{`
        .tqc-current { display: flex; align-items: center; gap: 8px; }
        .tqc-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .tqc-name { font-weight: 600; font-size: 0.88rem; color: var(--gm-text); }
        .tqc-badge { font-size: 0.65rem; font-weight: 600; padding: 1px 7px; border-radius: 99px; background: var(--gm-surface-2); color: var(--gm-text-muted); }
        .tqc-empty { font-size: 0.82rem; color: var(--gm-text-muted); font-style: italic; }
        .tqc-form { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; padding: 10px; border-radius: 10px; background: var(--gm-surface-2); }
        .tqc-check { display: flex; gap: 8px; align-items: flex-start; font-size: 0.8rem; color: var(--gm-text); cursor: pointer; }
        .tqc-check input { margin-top: 2px !important; }
        .tqc-check small { color: var(--gm-text-muted); font-size: 0.72rem; }
      `}</style>
    </div>
  )
}

export default TicketQueueCard
